'use client';

import React from 'react';
import { useRouter } from 'next/navigation';

import PageBreadcrumbs from '@/components/layout/PageBreadcrumbs';
import BannerFeatureCards from '@/components/sections/BannerServicePages/BannerFeatureCards';
import type {
  BaseHeroData,
  ButtonBlock,
  TextBlock,
} from '@/components/sections/BaseHeroSection/BaseHeroSection.types';
import PrimaryButton from '@/components/ui/Button/PrimaryButton';
import RichText from '@/components/ui/RichText/RichText';
import {
  ClinicDoctifyWidget,
  ClinicTrustPilotWidget,
} from '@/components/widgets/ClinicDoctifyWidget';
import { getAsset, getAssetForWidth } from '@/lib/assets';
import { useManifest } from '@/lib/manifest-context';

type TrustLogoImageItem = TextBlock & {
  type?: 'image';
  scale?: string;
};

type TrustLogoServiceItem =
  | {
      type: 'trustpilot';
      scale?: string;
    }
  | {
      type: 'doctify';
      scale?: string;
    };

export type TrustLogoItem = TrustLogoImageItem | TrustLogoServiceItem;

export type ManifestType = ReturnType<typeof useManifest>;

export function getDefaultTrustLogos(manifest: ManifestType): TrustLogoItem[] {
  return [
    {
      type: 'trustpilot',
      scale: 'scale-[0.6] md:scale-[0.6]',
    },
    {
      type: 'image',
      content: `[![Top Doctors](${getAsset(
        manifest,
        '/images/top-doctors-logo.png',
      )} "160x160")](https://www.topdoctors.co.uk/gh/lips-healthcare/)`,
      scale: 'scale-[0.9] md:scale-[1]',
    },
    {
      type: 'doctify',
      scale: 'scale-[0.8] md:scale-[0.8]',
    },
  ];
}

const BOOKING_WIDGET_TARGET_ID = 'booking-calendar-widget';

const ENQUIRY_FORM_TARGET_IDS = [
  'claim-form',
  'reserve-form',
  'service-enquiry-form',
  'enquiry-form',
  'enquire-form',
  'enquire-now-form',
  'serviceEnquiry',
  'pharmacist_advices_treatment_section',
  'love-day-form',
];

const BOOKING_FALLBACK_PATH = '/specialities?filter=all';
const BOOKING_SCROLL_OFFSET = 112;

function escapeSelectorValue(value: string): string {
  if (typeof CSS !== 'undefined' && typeof CSS.escape === 'function') {
    return CSS.escape(value);
  }

  return value.replace(/\\/g, '\\\\').replace(/"/g, '\\"');
}

function findTargetByIdOrTestId(id: string): HTMLElement | null {
  if (typeof document === 'undefined') {
    return null;
  }

  return (
    document.getElementById(id) ??
    document.querySelector<HTMLElement>(
      `[data-testid="${escapeSelectorValue(id)}"]`,
    )
  );
}

function findBookingWidgetTarget(): HTMLElement | null {
  return findTargetByIdOrTestId(BOOKING_WIDGET_TARGET_ID);
}

function findEnquiryFormTarget(): HTMLElement | null {
  for (const id of ENQUIRY_FORM_TARGET_IDS) {
    const target = findTargetByIdOrTestId(id);

    if (target) {
      return target;
    }
  }

  return null;
}

function findEnquireNowTrigger(): HTMLElement | null {
  if (typeof document === 'undefined') {
    return null;
  }

  const clickableElements = Array.from(
    document.querySelectorAll<HTMLElement>(
      'button, a, [role="button"], [data-testid]',
    ),
  );

  return (
    clickableElements.find((element) => {
      const text = element.textContent?.replace(/\s+/g, ' ').trim();

      return text?.toLowerCase() === 'enquire now';
    }) ?? null
  );
}

function scrollToTarget(target: HTMLElement): void {
  const top =
    target.getBoundingClientRect().top + window.scrollY - BOOKING_SCROLL_OFFSET;

  window.scrollTo({
    top,
    behavior: 'smooth',
  });

  const hadTabIndex = target.hasAttribute('tabindex');

  if (!hadTabIndex) {
    target.setAttribute('tabindex', '-1');
  }

  window.setTimeout(() => {
    target.focus({ preventScroll: true });

    if (!hadTabIndex) {
      target.removeAttribute('tabindex');
    }
  }, 350);
}
export const RenderTrustLogos = (
  trustLogosToRender: TrustLogoItem[],
  isLeft: boolean,
) => {
  return (
    trustLogosToRender.length > 0 && (
      <div
        data-testid="hero-trust-logos"
        className={`-mt-2 flex w-full flex-row flex-nowrap items-center gap-1 overflow-visible md:-mt-4 md:gap-3 ${
          isLeft ? 'justify-start' : 'justify-center'
        }`}
      >
        {trustLogosToRender.map((logo, index) => {
          if ('type' in logo && logo.type === 'trustpilot') {
            return (
              <div
                key={`trustpilot-${index}`}
                className="flex h-[110px] w-[110px] shrink-0 origin-center scale-[0.75] items-center justify-center md:w-[220px] md:scale-100"
              >
                <ClinicTrustPilotWidget scale={logo.scale} />
              </div>
            );
          }

          if ('type' in logo && logo.type === 'doctify') {
            return (
              <div
                key={`doctify-${index}`}
                className="relative flex h-[110px] w-[105px] shrink-0 origin-center -translate-y-1 scale-[0.65] items-center justify-center md:w-[220px] md:-translate-y-2 md:scale-[0.8]"
              >
                <ClinicDoctifyWidget scale={logo.scale} />
              </div>
            );
          }

          return (
            <div
              key={`logo-${index}`}
              className="flex h-[110px] w-[100px] shrink-0 origin-center scale-[0.95] items-center justify-center md:w-[220px] md:scale-100"
            >
              <RichText
                styles={{
                  a: 'flex h-full w-full items-center justify-center',
                  img: 'mt-4 h-auto max-h-[48px] w-auto max-w-full object-contain md:max-h-[56px]',
                  ...logo.styles,
                }}
              >
                {logo.content}
              </RichText>
            </div>
          );
        })}
      </div>
    )
  );
};

export interface BaseHeroSectionProps extends Omit<BaseHeroData, 'trustLogos'> {
  horizontalContentAlignment?: 'center' | 'left';
  trustLogos?: TrustLogoItem[];
  children?: React.ReactNode;
  sideContent?: React.ReactNode;
  testId?: string;
  className?: string;
  style?: React.CSSProperties;
  backgroundType?: 'gradient' | 'image';
  bgImageDesktop?: string;
  bgImageMobile?: string;
  onButtonClick?: (
    button: ButtonBlock,
    index: number,
    event: React.MouseEvent<HTMLAnchorElement | HTMLButtonElement>,
  ) => void;
}

const HeroGradientBlobs: React.FC = () => (
  <div
    aria-hidden="true"
    className="pointer-events-none absolute inset-0 overflow-hidden"
  >
    <div className="bg-theme-primary-light absolute bottom-[-5%] left-[3%] aspect-square w-[70%] rounded-full opacity-70 blur-[138px] sm:bottom-[-30%] sm:w-[32%]" />

    <div className="bg-lavender-300 absolute bottom-0 left-[23%] aspect-square w-[70%] rounded-full opacity-55 blur-[138px] sm:bottom-[-20%] sm:w-[36%]" />

    <div className="bg-theme-primary absolute bottom-[-5%] left-[40%] aspect-square w-[60%] rounded-full opacity-35 blur-[138px] sm:bottom-[-30%] sm:w-[36%]" />

    <div className="bg-info-300 absolute right-[5%] bottom-[-5%] aspect-square w-[70%] rounded-full opacity-65 blur-[138px] sm:bottom-[-30%] sm:w-[40%]" />
  </div>
);

const BaseHeroSection: React.FC<BaseHeroSectionProps> = ({
  heading,
  description,
  buttons,
  link,
  badge,
  trustLogos,
  textsContentData = [],
  featureCardsData = [],
  horizontalContentAlignment = 'center',
  children,
  sideContent,
  testId = 'base-hero-section',
  className,
  style,
  backgroundType = 'gradient',
  bgImageDesktop,
  bgImageMobile,
  onButtonClick,
}) => {
  const manifest = useManifest();
  const router = useRouter();
  const isLeft = horizontalContentAlignment === 'left';

  const trustLogosToRender =
    trustLogos === undefined ? getDefaultTrustLogos(manifest) : trustLogos;

  const contentColumnRef = React.useRef<HTMLDivElement>(null);
  const sideContentRef = React.useRef<HTMLDivElement>(null);
  const trustLogosRef = React.useRef<HTMLDivElement>(null);

  const [smallTabletThirdImageLeft, setSmallTabletThirdImageLeft] =
    React.useState('214px');

  const [smallTabletCollageScale, setSmallTabletCollageScale] =
    React.useState('1');

  React.useEffect(() => {
    const contentColumn = contentColumnRef.current;
    const sideContentElement = sideContentRef.current;

    if (
      !contentColumn ||
      !sideContentElement ||
      typeof window === 'undefined'
    ) {
      return undefined;
    }

    const mediaQuery =
      typeof window.matchMedia === 'function'
        ? window.matchMedia('(min-width: 820px) and (max-width: 900px)')
        : null;

    const updateThirdImageOffset = () => {
      if (mediaQuery && !mediaQuery.matches) {
        setSmallTabletThirdImageLeft('214px');
        setSmallTabletCollageScale('1');

        return;
      }

      const contentHeight = contentColumn.getBoundingClientRect().height;

      const trustLogosHeight =
        trustLogosRef.current?.getBoundingClientRect().height ?? 0;

      const textContentHeight = Math.max(0, contentHeight - trustLogosHeight);

      const extraTextHeight = Math.max(0, textContentHeight - 330);

      const shift = Math.min(54, extraTextHeight * 0.65);
      const scale = 1 + Math.min(0.12, extraTextHeight / 500);

      setSmallTabletThirdImageLeft(`${Math.round(214 - shift)}px`);

      setSmallTabletCollageScale(scale.toFixed(3));
    };

    updateThirdImageOffset();

    const resizeObserver =
      typeof ResizeObserver === 'function'
        ? new ResizeObserver(updateThirdImageOffset)
        : null;

    resizeObserver?.observe(contentColumn);
    resizeObserver?.observe(sideContentElement);

    if (trustLogosRef.current) {
      resizeObserver?.observe(trustLogosRef.current);
    }

    window.addEventListener('resize', updateThirdImageOffset);
    mediaQuery?.addEventListener('change', updateThirdImageOffset);

    return () => {
      resizeObserver?.disconnect();

      window.removeEventListener('resize', updateThirdImageOffset);

      mediaQuery?.removeEventListener('change', updateThirdImageOffset);
    };
  }, [sideContent]);

  const handleHeroButtonClick = React.useCallback(
    (
      event: React.MouseEvent<HTMLButtonElement | HTMLAnchorElement>,
      button: ButtonBlock,
      index: number,
    ) => {
      onButtonClick?.(button, index, event);

      if (event.defaultPrevented) return;

      if (button.link) return;

      event.preventDefault();

      const bookingWidgetTarget = findBookingWidgetTarget();

      if (bookingWidgetTarget) {
        scrollToTarget(bookingWidgetTarget);
        return;
      }

      const enquiryFormTarget = findEnquiryFormTarget();

      if (enquiryFormTarget) {
        scrollToTarget(enquiryFormTarget);
        return;
      }

      const enquireNowTrigger = findEnquireNowTrigger();

      if (enquireNowTrigger) {
        enquireNowTrigger.click();
        return;
      }

      router.push(BOOKING_FALLBACK_PATH);
    },
    [onButtonClick, router],
  );

  return (
    <>
      <section
        data-testid={testId}
        className={`no-scroll-reveal relative flex h-auto w-full flex-col justify-center bg-white lg:min-h-[638px] ${
          className || ''
        }`}
        style={style}
        data-header-dark={
          backgroundType === 'image' && (bgImageDesktop || bgImageMobile)
            ? 'true'
            : undefined
        }
      >
        {backgroundType === 'image' && bgImageDesktop ? (
          <>
            <div
              className="absolute inset-0 hidden bg-cover bg-center bg-no-repeat md:block"
              style={{
                // 1200w covers this hero at any reasonable desktop render
                // width with headroom for standard (2x) pixel density, without
                // requesting the full, much larger original — background-image
                // takes exactly one URL, so this can't use srcset directly.
                backgroundImage: `url(${getAssetForWidth(manifest, bgImageDesktop, 1200)})`,
              }}
            />

            {bgImageMobile && (
              <div
                className="absolute inset-0 bg-cover bg-center bg-no-repeat md:hidden"
                style={{
                  backgroundImage: `url(${getAssetForWidth(manifest, bgImageMobile, 768)})`,
                }}
              />
            )}

            <div
              aria-hidden="true"
              className="pointer-events-none absolute inset-0 bg-black/30"
            />
          </>
        ) : (
          <>
            <HeroGradientBlobs />

            <div
              aria-hidden="true"
              className="pointer-events-none absolute inset-x-0 top-0 h-24 bg-linear-to-b from-white to-transparent"
            />
          </>
        )}

        <div
          className={`relative z-50 mx-auto flex w-full gap-3 pt-4 pb-10 md:gap-18 lg:h-full lg:py-16 ${
            sideContent
              ? 'content-wrapper flex-row items-start lg:items-center xl:h-full xl:items-center xl:py-0'
              : 'max-w-4xl flex-col items-center justify-center lg:h-full'
          }`}
        >
          <div
            ref={contentColumnRef}
            className={`relative z-10 flex flex-col gap-3 md:gap-4 lg:gap-5 ${
              sideContent
                ? 'xl:gap-p-lg min-w-0 flex-1 lg:max-w-none xl:h-auto xl:w-[813px] xl:max-w-[813px] xl:flex-none xl:shrink-0'
                : 'w-full'
            } ${isLeft ? 'items-start text-left' : 'items-center text-center'}`}
          >
            {heading?.content && (
              <div
                data-testid="hero-heading"
                className="mt-10 w-full max-w-lg lg:mt-16 lg:max-w-[813px] xl:mt-0"
              >
                <RichText
                  styles={{
                    ...heading.styles,
                    h1: `text-[36px] font-bold leading-[44px] tracking-[-0.02em] text-text-headings capitalize md:text-[34px] md:leading-[42px] lg:text-[40px] lg:leading-[50px] xl:text-[48px] xl:leading-[58px] ${
                      heading.styles?.h1 ?? ''
                    }`,
                  }}
                >
                  {heading.content}
                </RichText>
              </div>
            )}

            {description?.content && (
              <div
                data-testid="hero-description"
                className="flex w-full max-w-md flex-col lg:max-w-[813px]"
              >
                {typeof description.content === 'string' ? (
                  <RichText
                    styles={{
                      p: 'w-full font-mulish text-[16px] leading-[24px] tracking-normal text-text-body md:text-[15px] md:leading-[22px] lg:text-[16px] lg:leading-[24px] xl:text-[17px] xl:leading-[28px]',
                      ...description.styles,
                    }}
                  >
                    {description.content}
                  </RichText>
                ) : (
                  <div className="font-mulish text-text-body w-full text-[16px] leading-[24px] tracking-normal md:text-[15px] md:leading-[22px] lg:text-[16px] lg:leading-[24px] xl:text-[17px] xl:leading-[28px]">
                    {description.content}
                  </div>
                )}
              </div>
            )}

            {textsContentData.length > 0 && (
              <div
                data-testid="hero-feature-cards"
                className="w-full max-w-md lg:max-w-[813px]"
              >
                <BannerFeatureCards cards={textsContentData} variant="chips" />
              </div>
            )}

            {featureCardsData.length > 0 && (
              <div
                data-testid="hero-feature-cards-boxed"
                className="w-full max-w-md px-4 md:max-w-220"
              >
                <BannerFeatureCards
                  cards={featureCardsData}
                  variant="feature"
                />
              </div>
            )}

            {buttons && buttons.length > 0 && (
              <div
                data-testid="hero-buttons"
                className={`flex w-full flex-row flex-wrap gap-3 md:w-auto ${
                  isLeft ? 'justify-start' : 'justify-center'
                }`}
              >
                {buttons && buttons.length > 0 && (
                  <div
                    data-testid="hero-buttons"
                    className={`flex w-full flex-row flex-wrap gap-3 md:w-auto ${
                      isLeft ? 'justify-start' : 'justify-center'
                    }`}
                  >
                    {buttons.map((button, index) => (
                      <PrimaryButton
                        key={`${button.content ?? 'hero-button'}-${index}`}
                        data={button}
                        widthFitContent
                        paddingClassName="px-6 py-3"
                        onClick={(event) =>
                          handleHeroButtonClick(
                            event as React.MouseEvent<
                              HTMLButtonElement | HTMLAnchorElement
                            >,
                            button,
                            index,
                          )
                        }
                      />
                    ))}
                  </div>
                )}
              </div>
            )}

            {badge?.content && (
              <div
                data-testid="hero-badge"
                className={`flex w-full flex-wrap items-center gap-2 min-[820px]:max-[900px]:w-[calc(100%+140px)] ${
                  isLeft ? 'justify-start' : 'justify-center'
                }`}
              >
                {badge.content
                  .split('|')
                  .map((part) => part.trim())
                  .filter(Boolean)
                  .map((text, index) => (
                    <div
                      key={`${text}-${index}`}
                      data-testid={`hero-badge-item-${index}`}
                      className={
                        badge.styles?.p ??
                        'border-theme-primary font-mulish text-theme-primary inline-flex items-center rounded-[8px] border bg-transparent px-4 py-2 text-xs leading-none font-medium whitespace-nowrap md:text-sm'
                      }
                    >
                      {text}
                    </div>
                  ))}
              </div>
            )}

            {link?.content && (
              <div data-testid="hero-link">
                <RichText
                  styles={{
                    a: 'font-mulish text-body-lg text-theme-primary underline underline-offset-2 transition-colors duration-200 hover:text-theme-primary-hover',
                    ...link.styles,
                  }}
                >
                  {link.content}
                </RichText>
              </div>
            )}

            {children}

            <div ref={trustLogosRef} className="w-full">
              {RenderTrustLogos(trustLogosToRender, isLeft)}
            </div>
          </div>

          {sideContent && (
            <div
              ref={sideContentRef}
              className="shrink-0 min-[820px]:mt-10 min-[820px]:w-[320px] min-[820px]:flex-none lg:mt-0 lg:w-[min(36%,433px)] xl:ml-[-39px] xl:h-[491px] xl:w-[433px] xl:flex-none xl:shrink-0"
              style={
                {
                  '--hero-small-tablet-third-left': smallTabletThirdImageLeft,
                  '--hero-small-tablet-collage-scale': smallTabletCollageScale,
                } as React.CSSProperties
              }
            >
              {sideContent}
            </div>
          )}
        </div>
      </section>
      <PageBreadcrumbs />
    </>
  );
};

export default BaseHeroSection;
